import { AsyncPipe } from '@angular/common';
import { Component, computed, effect, input, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { mergeMap, Observable, of, ReplaySubject } from 'rxjs';

import { UaService } from '@ecm/consumer';
import { Contract } from '@ecm/portfolio';
import { LinkService, RouterService } from '@ecm/router';

import { ClientTypeEnum } from '../../../../business/api/dashboard/forbidden/clientType.enum';
import { BrandEnum } from '../../../../business/api/dashboardV7/brandEnum';
import { ButtonViewModel } from '../../../../business/model/home/button.view-model';
import { ForbiddenViewModel } from '../../../../business/model/home/forbidden/forbidden.view-model';
import { AbstractParentComponent } from '../../../../components/shared/abstract-parent.component';
import { TrackingService } from '../../../../services/tracking.service';
import { UrlService } from '../../../../services/url.service';
import { ParamsUtils } from '../../../../utils/params.utils';
import { DefaultButtonComponent } from '../../component/default-button/default-button.component';
import { ForbiddenBannerComponent } from '../../component/forbidden-banner/forbidden-banner.component';
import { SuggestedComponent } from '../../component/suggested-button/suggested.component';
import { TerminatedComponent } from '../../component/terminated/terminated.component';
import { ForbiddenStoreService } from '../../store/forbidden-store-service';

@Component({
    selector: 'forbidden',
    templateUrl: 'forbidden.component.html',
    standalone: true,
    imports: [SuggestedComponent, TerminatedComponent, DefaultButtonComponent, ForbiddenBannerComponent, AsyncPipe],
})
export class ForbiddenComponent extends AbstractParentComponent implements OnChanges {
    public forbiddenType = input<ClientTypeEnum>();
    public contract = input<Contract>();
    public viewModel = input<ForbiddenViewModel>();
    public isSosh = input<boolean>();

    public isLinkDisabled$ = new ReplaySubject<boolean>();
    public messageMixed: string;

    // Waiting for eclair decision
    public isDisabledEclairForbiddenLink$ = computed(() =>
        this.urlService.isDisabledEclairLink(this.forbiddenStoreService.forbiddenViewModel.primaryButton?.link),
    );
    public isDisabledEclairESimLink: Observable<boolean>;
    public isDisabledEclairChangeAccountLink: Observable<boolean>;

    public forbiddenViewModel = computed(() => this.forbiddenStoreService.forbiddenViewModel);

    public constructor(
        protected uaService: UaService,
        protected routerService: RouterService,
        protected route: ActivatedRoute,
        protected forbiddenStoreService: ForbiddenStoreService,
        protected trackingService: TrackingService,
        protected linkService: LinkService,
        protected urlService: UrlService,
        private readonly translate: TranslateService,
    ) {
        super(uaService, routerService, route);
        effect(() => {
            this.forbiddenStoreService.enter(this.forbiddenType(), this.contract(), this.viewModel());
        });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.forbiddenType || changes.contract || changes.viewModel || changes.isSosh) {
            this.forbiddenStoreService.enter(this.forbiddenType(), this.contract(), this.viewModel());
        }
    }

    public openPrimaryButtonLink(buttonViewModel: ButtonViewModel, forbiddenViewModel?: ForbiddenViewModel) {
        const params = forbiddenViewModel ? ParamsUtils.cidMap(forbiddenViewModel.cid) : null;
        this.forbiddenStoreService.openLink(buttonViewModel, params);
    }

    public openSecondaryButtonLink(buttonViewModel: ButtonViewModel, forbiddenViewModel?: ForbiddenViewModel) {
        const params = forbiddenViewModel ? ParamsUtils.cidMap(forbiddenViewModel.cid) : null;
        this.forbiddenStoreService.openLink(buttonViewModel, params);
    }

    public getIcon(): Observable<string> {
        const dashboardTranslation = this.translate.instant('dashboard');
        return this.isSoshBrand().pipe(
            mergeMap((isSosh: boolean) => {
                let icon: string = null;
                if (!isSosh && this.contract()?.brand && this.contract()?.brand.toUpperCase() === BrandEnum.Sosh) {
                    icon = dashboardTranslation['universContract']['mixedSosh']['picto'];
                    this.messageMixed = dashboardTranslation['universContract']['mixedSosh']['message'];
                } else if (
                    isSosh &&
                    this.contract()?.brand &&
                    this.contract()?.brand.toUpperCase() === BrandEnum.Orange
                ) {
                    icon = dashboardTranslation['universContract']['mixedOrange']['picto'];
                    this.messageMixed = dashboardTranslation['universContract']['mixedOrange']['message'];
                }
                return of(icon);
            }),
        );
    }
}
